"use client"

import { useState, useEffect } from "react"
import { useSession } from "next-auth/react"
import { updateUserProfile } from "@/app/actions/update-user-profile"

interface ProfileEditModalProps {
  currentName: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: (newName: string) => void
}

export default function ProfileEditModal({ currentName, isOpen, onClose, onSuccess }: ProfileEditModalProps) {
  const [name, setName] = useState(currentName)
  const [error, setError] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const { update: updateSession } = useSession()

  // Update name when currentName changes or when modal opens
  useEffect(() => {
    if (isOpen) {
      setName(currentName)
      setError("")
    }
  }, [currentName, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      const result = await updateUserProfile(name.trim())

      if (result.success) {
        const newName = result.name || name.trim()

        // If tokens were refreshed, update the session
        if (result.tokensRefreshed && result.refreshedTokens) {
          await updateSession({
            refreshedTokens: result.refreshedTokens,
          })
        }

        onSuccess?.(newName)
        onClose()

        // Note: We don't need to reload the page anymore since the UI updates via onSuccess
      } else {
        setError(result.error || "An error occurred")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setName(currentName)
    setError("")
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">Edit Profile</h2>

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="mb-4">
            <label htmlFor="display-name" className="mb-2 block text-sm font-medium text-gray-700">
              Username
            </label>
            <input
              id="display-name"
              name="display-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              className="focus:ring-interest-blue focus:border-interest-blue w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
              autoComplete="nickname"
              autoFocus
              maxLength={60}
            />
            <p className="mt-1 text-xs text-gray-500">
              This is how your name will appear on comments and other content.
            </p>
          </div>

          <div className="mb-4 rounded-md bg-gray-50 p-3">
            <p className="text-sm text-gray-700">
              <strong>Privacy notice:</strong> We never share your email address on the site. Only your username is
              visible to other users.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || name.trim() === currentName}
              className="bg-interest-blue hover:bg-interest-darker-blue rounded-md px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
